import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { articles, categories } from "@/mocks/news";

interface NewsHeaderProps {
  onNavigate?: () => void;
}

export default function NewsHeader({ onNavigate }: NewsHeaderProps) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [showResult, setShowResult] = useState(false);

  const detectActive = (): string => {
    const p = location.pathname;
    if (p === "/") return "home";
    if (p.startsWith("/category/")) return p.split("/")[2];
    if (p.startsWith("/news/")) return "home";
    if (p === "/about") return "about";
    return "home";
  };
  const active = detectActive();

  useEffect(() => {
    if (onNavigate) onNavigate();
  }, [onNavigate]);

  const results = keyword.trim()
    ? articles
        .filter((a) => {
          const k = keyword.trim().toLowerCase();
          return (
            a.title.toLowerCase().includes(k) ||
            a.tags.join("").toLowerCase().includes(k) ||
            a.summary.toLowerCase().includes(k) ||
            categories.find((c) => c.slug === a.category)?.name.includes(keyword.trim())
          );
        })
        .slice(0, 6)
    : [];

  const catName = (slug: string) => categories.find((c) => c.slug === slug)?.name || "";

  return (
    <header className="sticky top-0 z-50">
      {/* 顶部细条 */}
      <div className="bg-primary-700 text-background-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 h-8 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <i className="ri-flashlight-fill text-accent-300"></i>
            <span className="opacity-95">今日快讯实时刷新 · 刚刚发生的最新爆料</span>
          </div>
          <div className="hidden md:flex items-center gap-4 text-background-50/90">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>2026 年 9 月 9 日
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line text-accent-300"></i>热搜榜
            </span>
          </div>
        </div>
      </div>

      {/* 主导航 */}
      <div className="bg-background-50/95 backdrop-blur border-b border-background-200/70 shadow-sm">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex items-center justify-between gap-4 h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group" aria-label="每日爆料首页">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-600 text-background-50 text-lg font-bold shadow-sm">
                爆
              </span>
              <span className="flex flex-col leading-none">
                <span className="font-heading text-xl font-black text-foreground-950 tracking-wide group-hover:text-primary-700 transition-colors">
                  每日爆料
                </span>
                <span className="text-[10px] text-foreground-500 mt-1 tracking-[0.2em]">每日快讯 · 新鲜猛料</span>
              </span>
            </Link>

            {/* 桌面导航 */}
            <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
              <Link
                to="/"
                className={`px-3 py-2 text-sm whitespace-nowrap rounded-md transition-colors ${
                  active === "home"
                    ? "bg-primary-600 text-background-50 font-medium"
                    : "text-foreground-800 hover:text-primary-700 hover:bg-primary-50"
                }`}
              >
                首页
              </Link>
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className={`px-3 py-2 text-sm whitespace-nowrap rounded-md transition-colors ${
                    active === c.slug
                      ? "bg-primary-600 text-background-50 font-medium"
                      : "text-foreground-800 hover:text-primary-700 hover:bg-primary-50"
                  }`}
                >
                  {c.name}
                </Link>
              ))}
              <Link
                to="/about"
                className={`px-3 py-2 text-sm whitespace-nowrap rounded-md transition-colors ${
                  active === "about"
                    ? "bg-primary-600 text-background-50 font-medium"
                    : "text-foreground-800 hover:text-primary-700 hover:bg-primary-50"
                }`}
              >
                关于我们
              </Link>
            </nav>

            {/* 右侧：搜索 + 投稿 */}
            <div className="flex items-center gap-3">
              <div className="relative hidden sm:block">
                <div className="flex items-center bg-background-100 border border-background-200 rounded-md pl-3 pr-1.5 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100 transition">
                  <i className="ri-search-line text-foreground-500 text-sm"></i>
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => {
                      setKeyword(e.target.value);
                      setShowResult(true);
                    }}
                    onFocus={() => setShowResult(true)}
                    placeholder="搜索今日快讯"
                    className="bg-transparent outline-none text-sm py-2 px-2 w-32 md:w-44 text-foreground-900 placeholder:text-foreground-400"
                  />
                  {keyword && (
                    <button
                      type="button"
                      aria-label="清空搜索"
                      onClick={() => {
                        setKeyword("");
                        setShowResult(false);
                      }}
                      className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-primary-600"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  )}
                </div>
                {showResult && keyword.trim() && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-background-50 border border-background-200 rounded-lg shadow-lg overflow-hidden fade-up">
                    {results.length === 0 ? (
                      <div className="px-4 py-6 text-sm text-foreground-500 text-center">
                        未找到与「{keyword}」相关的爆料
                      </div>
                    ) : (
                      <ul className="max-h-80 overflow-y-auto">
                        {results.map((r) => (
                          <li key={r.id}>
                            <Link
                              to={`/news/${r.id}`}
                              onClick={() => {
                                setShowResult(false);
                                setKeyword("");
                              }}
                              className="flex items-start gap-3 px-3 py-3 hover:bg-background-100 transition-colors"
                            >
                              <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center text-[10px] flex-shrink-0 font-medium">
                                {catName(r.category)}
                              </span>
                              <span className="text-sm text-foreground-800 line-clamp-2 leading-snug">{r.title}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <Link
                to="/about#contact"
                className="hidden sm:inline-flex items-center gap-1.5 bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm font-medium px-4 py-2.5 rounded-md whitespace-nowrap transition-colors"
              >
                <i className="ri-customer-service-2-line text-base"></i>
                我要爆料
              </Link>

              <button
                type="button"
                aria-label="打开菜单"
                onClick={() => setMenuOpen((v) => !v)}
                className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-800 hover:text-primary-700"
              >
                <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* 移动端搜索 */}
        <div className="sm:hidden px-4 pb-3">
          <div className="flex items-center bg-background-100 border border-background-200 rounded-md pl-3 pr-2">
            <i className="ri-search-line text-foreground-500 text-sm"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setShowResult(true);
              }}
              placeholder="搜索今日快讯"
              className="bg-transparent outline-none text-sm py-2 px-2 flex-1 text-foreground-900 placeholder:text-foreground-400"
            />
          </div>
        </div>

        {/* 移动端菜单 */}
        {menuOpen && (
          <nav className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-3 flex flex-col gap-1 fade-up" aria-label="移动端导航">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className={`px-3 py-2.5 text-sm rounded-md ${active === "home" ? "bg-primary-600 text-background-50" : "text-foreground-800 hover:bg-background-100"}`}
            >
              首页
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                onClick={() => setMenuOpen(false)}
                className={`px-3 py-2.5 text-sm rounded-md ${active === c.slug ? "bg-primary-600 text-background-50" : "text-foreground-800 hover:bg-background-100"}`}
              >
                {c.name}
              </Link>
            ))}
            <Link
              to="/about"
              onClick={() => setMenuOpen(false)}
              className={`px-3 py-2.5 text-sm rounded-md ${active === "about" ? "bg-primary-600 text-background-50" : "text-foreground-800 hover:bg-background-100"}`}
            >
              关于我们
            </Link>
            <Link
              to="/about#contact"
              onClick={() => setMenuOpen(false)}
              className="mt-1 inline-flex items-center justify-center gap-1.5 bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm font-medium px-4 py-2.5 rounded-md"
            >
              <i className="ri-customer-service-2-line text-base"></i>我要爆料
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}